import React, { useEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab, TextField, Button, Typography, Paper, FormGroup, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, Stack, Alert, CircularProgress } from '@mui/material';
import api from '../services/api';
import { migrateTasksToLocal } from '../services/tasksService';

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [signature, setSignature] = useState('');
  const [wysiwyg, setWysiwyg] = useState(true);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const userEditingRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [calendars, setCalendars] = useState<Array<{ id: string; summary: string; primary?: boolean }>>([]);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [tasklists, setTasklists] = useState<Array<{ id: string; title: string }>>([]);
  const [selectedTasklists, setSelectedTasklists] = useState<string[]>([]);
  const [migrating, setMigrating] = useState(false);
  const [migrationMsg, setMigrationMsg] = useState<{ type: 'success'|'info'|'error', text: string }|null>(null);

  // Ustawienia AI (frontend-only, localStorage)
  const [aiProvider, setAiProvider] = useState<string>('deepseek');
  const [aiApiKey, setAiApiKey] = useState<string>('');
  const [aiModel, setAiModel] = useState<string>('deepseek-chat');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string }|null>(null);

  useEffect(() => {
    const load = async () => {
      const res = await api.get('/auth/me');
      if (res.data?.success) {
        setSignature(res.data.data.email_signature || '');
        setSelectedCalendars(res.data.data.preferred_calendar_ids || []);
        // Preferencje list zadań Google nie są już używane w UI
        setSelectedTasklists(res.data.data.preferred_tasklist_ids || []);
      }
      try {
        const list = await api.get('/calendar/list');
        if (list.data?.success) setCalendars(list.data.data || []);
      } catch (e) {
        console.warn('Nie udało się pobrać listy kalendarzy', e);
      }
      // Wczytaj ustawienia AI z localStorage
      try {
        const p = localStorage.getItem('ai:provider') || 'deepseek';
        const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : p==='spark' ? 'gemma-4-31B-it' : 'claude-3-5-sonnet-latest');
        const k = localStorage.getItem(`ai:apiKey:${p}`) || '';
        setAiProvider(p);
        setAiModel(m);
        setAiApiKey(k);
      } catch {}
    };
    load();
  }, []);

  useEffect(() => {
    if (wysiwyg && editorRef.current && !userEditingRef.current) {
      editorRef.current.innerHTML = signature || '';
    }
  }, [wysiwyg, signature]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = wysiwyg ? (editorRef.current?.innerHTML || '') : signature;
      await api.put('/auth/me', { email_signature: payload, preferred_calendar_ids: selectedCalendars, preferred_tasklist_ids: selectedTasklists });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAI = () => {
    try {
      localStorage.setItem('ai:provider', aiProvider);
      localStorage.setItem(`ai:apiKey:${aiProvider}`, aiApiKey);
      localStorage.setItem(`ai:model:${aiProvider}`, aiModel);
      setTestResult({ ok: true, message: 'Zapisano ustawienia lokalnie.' });
      try { window.dispatchEvent(new CustomEvent('ai:settings:changed')); } catch {}
    } catch {}
  };

  const handleTestAI = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const resp = await api.post(
        '/ai/test-credentials',
        { provider: aiProvider, apiKey: aiApiKey, model: aiModel },
        { timeout: 120000 }
      );
      if (resp.data?.success) {
        setTestResult({ ok: true, message: `OK: ${resp.data?.data?.content || ''}` });
      } else {
        setTestResult({ ok: false, message: resp.data?.message || 'Test nieudany' });
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Błąd testu';
      setTestResult({ ok: false, message: msg });
    } finally {
      setTestLoading(false);
    }
  };

  const handleMigrateTasks = async () => {
    setMigrating(true);
    setMigrationMsg(null);
    try {
      const res = await migrateTasksToLocal();
      if (res?.alreadyMigrated) {
        setMigrationMsg({ type: 'info', text: 'Zadania były już wcześniej zmigrowane.' });
      } else {
        const createdLists = Number.isFinite(res?.createdLists) ? res!.createdLists : 0;
        const createdTasks = Number.isFinite(res?.createdTasks) ? res!.createdTasks : 0;
        setMigrationMsg({ type: 'success', text: `Migracja zakończona. Utworzono list: ${createdLists}, zadań: ${createdTasks}.` });
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Błąd migracji';
      setMigrationMsg({ type: 'error', text: msg });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Mój profil</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Dane" />
        <Tab label="Stopka e‑mail" />
        <Tab label="Kalendarze Google" />
        <Tab label="Zadania" />
        <Tab label="Asystent AI" />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <Typography variant="body2">Sekcja danych – w przygotowaniu.</Typography>
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <Typography variant="body2" sx={{ mb: 1 }}>Edytuj stopkę. Będzie dołączana do wszystkich wiadomości generowanych przez system.</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small" onClick={() => setWysiwyg(!wysiwyg)}>
            {wysiwyg ? 'Pokaż HTML' : 'Tryb edycji (WYSIWYG)'}
          </Button>
        </Box>
        {wysiwyg ? (
          <Box
            sx={{ border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1, p: 1.5, minHeight: 200, backgroundColor: '#fff' }}
            contentEditable
            suppressContentEditableWarning
            ref={editorRef}
            onInput={(e) => {
              userEditingRef.current = true;
              setSignature((e.target as HTMLElement).innerHTML);
              requestAnimationFrame(() => { userEditingRef.current = false; });
            }}
          />
        ) : (
          <TextField
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            fullWidth
            multiline
            minRows={8}
            placeholder="Wprowadź HTML stopki"
          />
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleSave} disabled={loading}>Zapisz</Button>
        </Box>
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <Typography variant="body2" sx={{ mb: 1 }}>Wybierz kalendarze, z których chcesz widzieć wydarzenia na stronie głównej.</Typography>
        <FormGroup>
          {calendars.length === 0 && (
            <Typography variant="body2" color="text.secondary">Brak dostępnych kalendarzy lub brak zgody na dostęp. Wyloguj i zaloguj się ponownie.</Typography>
          )}
          {calendars.map((c) => (
            <FormControlLabel
              key={c.id}
              control={<Checkbox checked={selectedCalendars.includes(c.id)} onChange={(e) => {
                const checked = e.target.checked;
                setSelectedCalendars((prev) => checked ? Array.from(new Set([...prev, c.id])) : prev.filter(id => id !== c.id));
              }} />}
              label={`${c.summary}${c.primary ? ' (główny)' : ''}`}
            />
          ))}
        </FormGroup>
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleSave} disabled={loading}>Zapisz</Button>
        </Box>
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Aplikacja korzysta teraz z wewnętrznej listy zadań Unitresco (jedna lista na użytkownika).
          W tym miejscu możesz jednorazowo przenieść swoje zadania z Google do Unitresco.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="outlined" color="secondary" onClick={handleMigrateTasks} disabled={migrating}>
            {migrating ? (<><CircularProgress size={18} sx={{ mr: 1 }} /> Migruję…</>) : 'Migruj zadania z Google do Unitresco'}
          </Button>
        </Stack>
        {migrationMsg && (
          <Alert severity={migrationMsg.type} sx={{ mt: 2 }}>{migrationMsg.text}</Alert>
        )}
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <Typography variant="body2" sx={{ mb: 2 }}>Konfiguracja dotyczy wyłącznie frontendu (zapisywana lokalnie w przeglądarce).</Typography>
        <Stack spacing={2} sx={{ maxWidth: 520 }}>
          <FormControl fullWidth>
            <InputLabel id="ai-provider-label">Dostawca</InputLabel>
            <Select labelId="ai-provider-label" label="Dostawca" value={aiProvider} onChange={(e)=>{
              const p = String(e.target.value);
              setAiProvider(p);
              try {
                const m = localStorage.getItem(`ai:model:${p}`) || (p==='deepseek' ? 'deepseek-chat' : p==='spark' ? 'gemma-4-31B-it' : 'claude-3-5-sonnet-latest');
                const k = localStorage.getItem(`ai:apiKey:${p}`) || '';
                setAiModel(m);
                setAiApiKey(k);
              } catch {}
            }}>
              <MenuItem value="deepseek">DeepSeek</MenuItem>
              <MenuItem value="anthropic">Anthropic</MenuItem>
              <MenuItem value="spark">Spark Gemma (Tailscale, bez logowania w czacie)</MenuItem>
            </Select>
          </FormControl>

          {aiProvider !== 'spark' && (
          <TextField
            label="API Key"
            type="password"
            value={aiApiKey}
            onChange={(e)=>setAiApiKey(e.target.value)}
            helperText="Klucz przechowywany lokalnie w localStorage"
            fullWidth
          />
          )}
          {aiProvider === 'spark' && (
            <Typography variant="body2" color="text.secondary">
              Połączenie z modelem odbywa się przez backend (SPARK_GEMMA_* dla 31B, SPARK_GEMMA_26B_* dla 26B, SPARK_QWEN_* dla Qwen). Wybór modelu wysyła nagłówek x-ai-model. W czacie publicznym — bez JWT. Przy dużych promptach na Qwen warto zwiększyć AI_CHAT_TIMEOUT_MS po stronie backendu.
            </Typography>
          )}

          <FormControl fullWidth>
            <InputLabel id="ai-model-label">Model</InputLabel>
            <Select labelId="ai-model-label" label="Model" value={aiModel} onChange={(e)=>setAiModel(String(e.target.value))}>
              {aiProvider==='deepseek' ? (
                [
                  <MenuItem key="deepseek-chat" value="deepseek-chat">deepseek-chat</MenuItem>,
                  <MenuItem key="deepseek-reasoner" value="deepseek-reasoner">deepseek-reasoner</MenuItem>
                ]
              ) : aiProvider==='spark' ? (
                [
                  <MenuItem key="gemma-4-31B-it" value="gemma-4-31B-it">gemma-4-31B-it (31B, :30000)</MenuItem>,
                  <MenuItem key="gemma-4-26b-a4b-it" value="gemma-4-26b-a4b-it">gemma-4-26b-a4b-it (26B A4B, :30001)</MenuItem>,
                  <MenuItem key="Qwen3.6-35B-A3B" value="Qwen3.6-35B-A3B">Qwen3.6-35B-A3B (vision, :8085)</MenuItem>,
                ]
              ) : (
                [
                  <MenuItem key="claude-3-5-sonnet-latest" value="claude-3-5-sonnet-latest">claude-3.5-sonnet</MenuItem>,
                  <MenuItem key="claude-3-sonnet-20240229" value="claude-3-sonnet-20240229">claude-3-sonnet-20240229</MenuItem>
                ]
              )}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleSaveAI}>Zapisz ustawienia AI</Button>
            <Button variant="outlined" onClick={handleTestAI} disabled={testLoading || (aiProvider === 'anthropic' && !aiApiKey)}>Przetestuj</Button>
          </Stack>
          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'error'}>{testResult.message}</Alert>
          )}
        </Stack>
      </TabPanel>
    </Paper>
  );
};

export default ProfilePage;


